import { Divider, Typography } from '@mui/material';


import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  KeyValue,
  Pill,
  Progress,
  SectionHeader,
  TitleHeader,
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { useGetJobStatusRecordDetails } from '../../api-refactored/job-status-report/useGetJobStatusRecordDetails';
import { useGetUserAuthorities } from '../../api-refactored/user/useGetUserAuthorities';
import RouteKey from '../../routes/key';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  halfWidthLeftItem: {
    width: '50%',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
    },
  },
  halfWidthRightItem: {
    width: '50%',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
    },
  },
  fullWidthLeftItem: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
    },
  },
  formElementContainer: {
    width: '100%',
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  divider: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
    borderWidth: 0,
    borderTopWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.color.border.borderAlt,
  },
  headerBelowDivider: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
    },
    paddingBottom: theme.spacing(4),
  },
  backToListButton: {
    marginTop: theme.spacing(4),
  },
}));

const { admin } = RouteKey.child;
const { jobStatusReport } = RouteKey.admin.child;
const { error } = RouteKey.child;
const { generic } = RouteKey.error.child;

const JobStatusRecordDetails = () => {
  const { state: tableViewState } = useLocation();
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { uuid } = useParams<{ uuid: string }>();
  const history = useHistory();
  const [hasView, setHasView] = useState<boolean>(false);

  const { data: authorities, isLoading: isLoadingAuthorities } = useGetUserAuthorities();

  const {
    data: jobStatusRecordDetails,
    isLoading: isLoadingJobStatusRecordDetails,
    isFetching: isFetchingJobStatusRecordDetails,
  } = useGetJobStatusRecordDetails({ uuid, errorDetailsOnly: false });

  const recordStatusPillColor = (recordStatusCode?: string) => {
    if (recordStatusCode)
      return recordStatusCode.toUpperCase() === 'FAILED' ? 'negative' : 'positive';
    return 'default';
  };

  const formatDateDefaultDash = (dateFormat: string, date?: string) => {
    if (date) {
      return dayjs(date).format(dateFormat);
    }
    return '-';
  };

  useEffect(() => {
    if (isLoadingAuthorities) {
      setHasView(false);
    } else if (
      !isLoadingAuthorities &&
      !authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_JOB_STATUS_REPORT_VIEW)
    ) {
      setHasView(false);
      history.push(`/${error}/${generic}`);
    } else setHasView(true);

    if (isLoadingJobStatusRecordDetails) {
      setHasView(false);
    } else {
      if (!authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_JOB_STATUS_REPORT_VIEW))
        setHasView(false);
      setHasView(true);
    }
    if (isFetchingJobStatusRecordDetails) {
      setHasView(false);
    }
  }, [
    history,
    authorities,
    isLoadingAuthorities,
    isLoadingJobStatusRecordDetails,
    isFetchingJobStatusRecordDetails,
  ]);

  return (
    <>
      <TitleHeader title={t('admin->>JobStatusReport.RecordDetails.Title')} />
      {!isLoadingAuthorities && hasView ? (
        <>
          <ContentTemplate>
            <Box className={classes.responsiveFlexContainer}>
              <SectionHeader
                className={classes.formElementContainer}
                title={t('admin->>JobStatusReport.RecordDetails.MainHeader')}
              />
              <Box className={classes.halfWidthLeftItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.RecordDetails.NricFinPassportIndexNo')}
                  valueText={jobStatusRecordDetails?.uinfinPassportIndexNo ?? '-'}
                />
              </Box>
              <Box className={classes.halfWidthRightItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.RecordDetails.Name')}
                  valueText={jobStatusRecordDetails?.name ?? '-'}
                />
              </Box>
              <Box className={classes.fullWidthLeftItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.RecordDetails.ExamLevel')}
                  valueText={jobStatusRecordDetails?.examLevel ?? '-'}
                />
              </Box>
              <Box className={classes.halfWidthLeftItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.RecordDetails.ApplicationDate')}
                  valueText={formatDateDefaultDash(
                    'D MMM YYYY',
                    jobStatusRecordDetails?.applicationDateTime
                  )}
                />
              </Box>
              <Box className={classes.halfWidthRightItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.ColumnApplicationId')}
                  valueText={jobStatusRecordDetails?.applicationId ?? '-'}
                />
              </Box>
            </Box>
            <Divider className={classes.divider} />
            <Typography className={classes.headerBelowDivider} variant="h3">
              {t('admin->>JobStatusReport.RecordDetails.JobDetails')}
            </Typography>
            <Box className={classes.responsiveFlexContainer}>
              <Box className={classes.halfWidthLeftItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.RecordDetails.ErrorDescription')}
                  valueText={
                    jobStatusRecordDetails?.recordStatusErrorModel?.errorDescription ?? '-'
                  }
                />
              </Box>
              <Box className={classes.halfWidthRightItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.ColumnSyncAttemptDate')}
                  valueText={formatDateDefaultDash(
                    'D MMM YYYY HH:mm',
                    jobStatusRecordDetails?.jobStatusItemResponseModel?.syncAttemptDate
                  )}
                />
              </Box>
              <Box className={classes.halfWidthLeftItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.ColumnStatus')}
                  valueText={
                    <Pill
                      variant={recordStatusPillColor(
                        jobStatusRecordDetails?.jobStatusItemResponseModel?.recordStatus?.code
                      )}
                      label={
                        jobStatusRecordDetails?.jobStatusItemResponseModel?.recordStatus
                          ?.description ?? ''
                      }
                    />
                  }
                />
              </Box>
              <Box className={classes.halfWidthRightItem}>
                <KeyValue
                  keyText={t('admin->>JobStatusReport.RecordDetails.MaxAttempt')}
                  valueText={
                    jobStatusRecordDetails?.jobStatusItemResponseModel?.noOfAttempts ?? '-'
                  }
                />
              </Box>
            </Box>
            <Button
              className={classes.backToListButton}
              variant="outlined"
              onClick={() => history.push(`/${admin}/${jobStatusReport.route}`, tableViewState)}
            >
              Back to List
            </Button>
          </ContentTemplate>
        </>
      ) : (
        <ContentTemplate>
          <Progress size="XL" color="secondary" />
        </ContentTemplate>
      )}
    </>
  );
};

export default JobStatusRecordDetails;
